import React, { useState, useEffect } from 'react';
import { NavLink as Link } from 'react-router-dom';
import { Nav, NavItem, NavLink, Tooltip } from 'reactstrap';
import styled from 'styled-components';

import Icon from 'shared/components/Icon';
import {
  dashboardPath,
  portfoliosPath,
  projectsPath,
  subscribersPath,
  utilitiesPath
} from 'shared/utilities/AdminPathHelper';
import { toggleNav } from 'shared/store/modules/nav/actions';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'shared/hooks/useAppDispatch';
import { css } from '@emotion/react';
import { getIsNavOpen } from 'shared/store/modules/nav/selectors';

const NavIcon = styled(Icon)`
  margin-right: 10px;
`;

interface AdminNavigationProps {
  navigationIsCollapsed: boolean;
}

const styles = css`
  @media (min-width: 1200px) {
    .collapsed {
      display: none;
    }
  }
`;

const navTooltipId = (text: string) => `nav-tooltip-${text.toLowerCase().replace(/\s+/g, '-')}`;

export const AdminNavigation = ({ navigationIsCollapsed }: AdminNavigationProps) => {
  // @ts-ignore
  const dispatch = useAppDispatch();
  const isNavOpen = useSelector(getIsNavOpen);
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 1200px)').matches);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1200px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <>
      <Nav className="navigation-primary" vertical css={styles}>
        {[
          { path: dashboardPath(), icon: 'dashboard', text: 'Dashboard' },
          { path: portfoliosPath(), icon: 'folder', text: 'Portfolios' },
          { path: projectsPath(), icon: 'layers', text: 'Projects' },
          { path: subscribersPath(), icon: 'people', text: 'Subscribers' },
          { path: utilitiesPath(), icon: 'location_city', text: 'Utilities' },
          { path: '/admin/promos', icon: 'campaign', text: 'Promos' }
        ].map(({ path, icon, text }) => (
          <React.Fragment key={path}>
            <NavItem id={navTooltipId(text)} onClick={() => (isNavOpen ? dispatch(toggleNav()) : {})}>
              <NavLink end={path === dashboardPath()} tag={Link} to={path}>
                <NavIcon icon={icon} />
                <span className={`nav-text ${navigationIsCollapsed ? 'collapsed' : ''}`}>{text}</span>
              </NavLink>
            </NavItem>
            {navigationIsCollapsed && isDesktop && (
              <Tooltip
                target={navTooltipId(text)}
                isOpen={openTooltip === text}
                toggle={() => setOpenTooltip(prev => (prev === text ? null : text))}
                placement="right"
              >
                {text}
              </Tooltip>
            )}
          </React.Fragment>
        ))}
      </Nav>

      <Nav className="navigation-secondary" vertical css={styles}>
        {[
          { path: '/admin/invoices', icon: 'assignment', text: 'Statements' },
          { path: '/admin/reports', icon: 'grid_on', text: 'Reports' },
          { path: '/admin/tools', icon: 'build', text: 'Tools' },
          { path: '/admin/settings', icon: 'settings', text: 'Settings' }
        ]
          .filter(Boolean)
          .map(item => {
            const { path, icon, text } = item as { path: string; icon: string; text: string };
            return (
              <React.Fragment key={path}>
                <NavItem id={navTooltipId(text)} onClick={() => (isNavOpen ? dispatch(toggleNav()) : {})}>
                  <NavLink tag={Link} to={path}>
                    <NavIcon icon={icon} />{' '}
                    <span className={`nav-text ${navigationIsCollapsed ? 'collapsed' : ''}`}>{text}</span>
                  </NavLink>
                </NavItem>
                {navigationIsCollapsed && isDesktop && (
                  <Tooltip
                    target={navTooltipId(text)}
                    isOpen={openTooltip === text}
                    toggle={() => setOpenTooltip(prev => (prev === text ? null : text))}
                    placement="right"
                  >
                    {text}
                  </Tooltip>
                )}
              </React.Fragment>
            );
          })}
      </Nav>
    </>
  );
};
